import { Injectable, Signal, inject } from '@angular/core';
import { BuildType, CompaniesQueryParams, Company, LicensesPoolType, PagedResponse } from 'mbs-ui-shared@models';
import { CompaniesService, SupportLicense } from 'mbs-ui-shared@services/companies.service';
import { Observable, tap } from 'rxjs';
import { CompaniesStore } from '../store/companies/companiesStore';
import { PagingAndDataFacade } from '../store/withPagingAndDataStoreFeature/pagingAndData.facade';
import { ComputersFacade } from './computers.facade';
import { DomainsFacade } from './domains.facade';

@Injectable({
  providedIn: 'root'
})
export class CompaniesFacade extends PagingAndDataFacade<Company> {
  protected override store = inject(CompaniesStore);
  private companiesService = inject(CompaniesService);
  private computersFacade = inject(ComputersFacade);
  private domainsFacade = inject(DomainsFacade);

  // Custom signal exposed directly
  get defaultCompany(): Signal<Company | undefined> {
    return this.store.selectDefaultCompany;
  }

  getCompany(id: string): Observable<Company> {
    return this.companiesService.getById(id);
  }

  getCompanies(params?: CompaniesQueryParams): Observable<PagedResponse<Company>> {
    return this.companiesService.get(params);
  }

  getCompaniesCompact(params?: CompaniesQueryParams): Observable<PagedResponse<Company>> {
    return this.companiesService.getCompact(params);
  }

  getBuildsMapLicenses(): Observable<SupportLicense> {
    return this.companiesService.getBuildsMapLicenses();
  }

  getSupportBuilds(): Observable<BuildType[]> {
    return this.companiesService.getSupportBuilds();
  }

  assignLicensePoolTypeToCompany(licensePoolType: LicensesPoolType, companyId: string): Observable<unknown> {
    return this.companiesService.assignLicensePoolTypeToCompany(licensePoolType, companyId);
  }

  create(company: Company): Observable<Company> {
    return this.companiesService.create(company).pipe(tap(() => this.invalidateEntities(false)));
  }

  update(company: Company): Observable<Company> {
    return this.companiesService.update(company).pipe(tap((company) => this.invalidateEntities(true, company)));
  }

  delete(company: Company): Observable<null> {
    return this.companiesService.deleteCompany(company?.id).pipe(tap(() => this.invalidateEntities(true)));
  }

  private invalidateEntities(invalidateOther: boolean, company?: Company): void {
    this.refreshAfterCRUD(company);
    if (invalidateOther) {
      this.computersFacade.invalidate();
      this.domainsFacade.invalidate();
    }
  }
}
